import { Container, makeStyles, Typography } from '@material-ui/core'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getServiceData } from '../../../actions/households/households.actions'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { formatMembershipType } from '../../../utils'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		paddingTop: '24px',
		paddingLeft: '20px',
		paddingRight: '20px',
		paddingBottom: '75px'
	},
	wrapper: {
		display: 'flex',
		flexWrap: 'wrap'
	},
	left: {
		flex: 1,
		paddingRight: 24,
		[theme.breakpoints.down(1375)]: {
			paddingRight: 0
		}
	},
	right: {
		minWidth: 369
	},
	header: {
		marginBottom: 24
	},
	heading: {
		marginTop: 32,
		marginBottom: 26
	},
	trackedDataWrapper: {
		width: '100%',
		background: '#F3F3F3',
		borderRadius: '6px',
		marginBottom: '24px',
		padding: '24px 23px',
		display: 'flex',
		flexDirection: 'column'
	},
	title: {
		fontSize: '16px',
		fontWeight: 800,
		lineHeight: '22px',
		textTransform: 'capitalize'
	},
	trackedData: {
		display: 'flex',
		width: '670px',
		'& .section:not(:first-child)': {
			paddingLeft: '24px',
			position: 'relative',
			flex: '0 0 190px',
			'&:not(:first-child):before': {
				content: "''",
				height: '100%',
				width: '1px',
				position: 'absolute',
				top: 0,
				left: '0px',
				background: '#D0CFD2'
			}
		},
		'& .section:nth-child(1)': {
			flex: '0 0 158px'
		},
		'& .section:nth-child(4)': {
			flex: '0 0 122px'
		}
	},
	dataHeading: {
		lineHeight: '16px',
		marginBottom: '4px'
	},
	data: {
		fontSize: '22px',
		fontWeight: 800,
		lineHeight: '30px',
		textTransform: 'uppercase'
	},
	permissionsData: {
		fontSize: '22px',
		fontWeight: 800,
		lineHeight: '30px',
		textTransform: 'capitalize'
	},
	aggregate: {
		padding: '4px 11.5px 4px 10px',
		display: 'flex',
		justifyContent: 'space-between',
		background: '#F9F9F9',
		borderRadius: '6px'
	},
	aggregateText: {
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '19px'
	}
}))

const HouseholdData = () => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const household = useSelector((state) => state.specific.households.selected)
	const facility = useSelector((state) => state.specific.facilities.selected)
	const loading = useSelector((state) => state.specific.households.loading)

	useEffect(() => {
		if (household && !loading && !household.service_lines_data) {
			dispatch(getServiceData(household._id))
		}
	}, [household])

	let latest_dates =
		household && household.service_lines_data
			? Object.keys(household.service_lines_data)
					.filter((line) => line !== 'overview')
					.map((line) => household.service_lines_data[line].latest)
					.filter(Boolean)
					.sort((a, b) => moment(b).toDate() - moment(a).toDate())
			: null

	const getMembershipType = () => {
		let memberships =
			household.memberships && household.memberships.length
				? household.memberships.filter(
						(m) => !m.deactivated && !m.expired && moment(m.end_date).isAfter(moment().startOf('day'))
				  )
				: null

		let type =
			memberships && memberships.length ? memberships.sort((a, b) => b.createdAt - a.createdAt)[0].type : ''

		type = formatMembershipType(type)

		return !type ? 'none' : type === 'monthly' ? 'Monthly Pass' : `${type} membership`
	}

	return (
		<Container className={classes.root}>
			<div className={classes.header}>
				<Typography variant="h5" color="primary" style={{ fontWeight: 800 }}>
					Household Data
				</Typography>
			</div>
			{household && household.service_lines_data ? (
				<div className={classes.wrapper}>
					<div className={classes.left}>
						<div className={classes.trackedDataWrapper}>
							<div style={{ marginBottom: 16 }}>
								<Typography className={classes.title}>Overview</Typography>
							</div>
							<div className={classes.trackedData} style={{ marginBottom: 24 }}>
								<div className="section">
									<Typography className={classes.dataHeading} variant="overline">
										First Signed up
									</Typography>
									<Typography className={classes.data}>
										{household.createdAt
											? moment(household.createdAt).format('MM/DD/YYYY')
											: 'Unknown'}
									</Typography>
								</div>
								<div className="section">
									<Typography className={classes.dataHeading} variant="overline">
										Last Interaction
									</Typography>
									<Typography className={classes.data}>
										{latest_dates && latest_dates[0]
											? moment(latest_dates[0]).format('MM/DD/YYYY')
											: 'No Interactions'}
									</Typography>
								</div>
								<div className="section">
									<Typography className={classes.dataHeading} variant="overline">
										Total Interactions
									</Typography>
									<Typography className={classes.data}>
										{household.service_lines_data.overview.total_interactions || 0}
									</Typography>
								</div>
							</div>
							<div className={classes.aggregate}>
								<Typography className={classes.aggregateText}>Lifetime Spent</Typography>
								<Typography className={classes.aggregateText}>
									$
									{Number(
										(household.balances && household.balances.lifetime
											? household.balances.lifetime
											: 0) / 100
									).toFixed(2)}
								</Typography>
							</div>
						</div>
						<div className={classes.heading}>
							<Typography
								variant="h5"
								color="primary"
								style={{
									fontWeight: 800,
									textTransform: 'uppercase',
									fontSize: '18px',
									lineHeight: '25px'
								}}>
								Service Lines
							</Typography>
						</div>
						<div>
							{Object.keys(household.service_lines_data)
								.filter((line) => line !== 'overview')
								.map((line) => {
									let title =
										line === 'bar_park'
											? 'Bar & Park'
											: line === 'home'
											? 'Home Services'
											: line
									let service = household.service_lines_data[line]

									return (
										<div className={classes.trackedDataWrapper}>
											<div style={{ marginBottom: 16 }}>
												<Typography className={classes.title}>{title}</Typography>
											</div>
											<div className={classes.trackedData}>
												<div className="section">
													<Typography className={classes.dataHeading} variant="overline">
														First {line !== 'bar_park' ? 'Appointment' : 'Visit'}
													</Typography>
													<Typography className={classes.data}>
														{service.first
															? moment(service.first).format('MM/DD/YYYY')
															: 'Never Used'}
													</Typography>
												</div>
												<div className="section">
													<Typography className={classes.dataHeading} variant="overline">
														Last {line !== 'bar_park' ? 'Appointment' : 'Visit'}
													</Typography>
													<Typography className={classes.data}>
														{service.latest
															? moment(service.latest).format('MM/DD/YYYY')
															: 'Never Used'}
													</Typography>
												</div>
												<div className="section">
													<Typography className={classes.dataHeading} variant="overline">
														Total {line !== 'bar_park' ? 'Appointments' : 'Visits'}
													</Typography>
													<Typography className={classes.data}>
														{service.total || 0}
													</Typography>
												</div>
												{line === 'boarding' ? (
													<div className="section">
														<Typography
															className={classes.dataHeading}
															variant="overline">
															Total Nights
														</Typography>
														<Typography className={classes.data}>
															{service.total_nights || 0}
														</Typography>
													</div>
												) : null}
											</div>
											{['boarding', 'daycare'].includes(line) ? (
												<div>
													<div className={classes.aggregate} style={{ marginTop: 24 }}>
														<Typography className={classes.aggregateText}>
															Average Number of Dogs
														</Typography>
														<Typography className={classes.aggregateText}>
															{service.average_num_of_pets || 0}
														</Typography>
													</div>
													{line === 'boarding' ? (
														<div
															className={classes.aggregate}
															style={{ marginTop: 8 }}>
															<Typography className={classes.aggregateText}>
																Average Appointment Length
															</Typography>
															<Typography className={classes.aggregateText}>
																{service.average_num_of_days || 0} Night
																{service.average_num_of_days !== 1 ? 's' : ''}
															</Typography>
														</div>
													) : null}
												</div>
											) : null}
										</div>
									)
								})}
						</div>
					</div>
					<div className={classes.right}>
						{facility && facility.skiptown ? (
							<div className={classes.trackedDataWrapper} style={{ marginBottom: 8 }}>
								<Typography variant="overline">Bar & Park Access</Typography>
								<Typography className={classes.permissionsData}>{getMembershipType()}</Typography>
							</div>
						) : null}
						<div className={classes.trackedDataWrapper} style={{ marginBottom: 8 }}>
							<Typography variant="overline">Compatibility Status</Typography>
							<Typography className={classes.permissionsData}>
								{household.pets &&
								household.pets.length &&
								household.pets.some((p) => p.temperament_test_status === 'Passed')
									? 'Completed'
									: 'Not Completed'}
							</Typography>
						</div>
						<div className={classes.trackedDataWrapper} style={{ marginBottom: 8 }}>
							<Typography variant="overline">Consultation Status</Typography>
							<Typography
								className={classes.permissionsData}
								style={{ textTransform: 'capitalize' }}>
								{household.consultation_status && household.consultation_status === 'completed'
									? 'completed'
									: 'not completed'}
							</Typography>
						</div>
						<div className={classes.trackedDataWrapper}>
							<Typography variant="overline">Home Services Status</Typography>
							<Typography className={classes.permissionsData}>
								{household.dog_walking && household.dog_walking === 'in_area'
									? 'In Area'
									: 'Not In Area'}
							</Typography>
						</div>
					</div>
				</div>
			) : null}
			{loading || (household && !household.service_lines_data) ? <LoadingOverlay /> : null}
		</Container>
	)
}

export default HouseholdData
